<template>
  <section id="solutions" class="sol">
    <div class="sol-head">
      <h2 class="display" ref="head">
        <span class="ln"><span>Computer Service</span></span>
        <span class="ln"><span>Solutions</span></span>
      </h2>
      <div class="arrows">
        <button @click="scrollBy(-1)" aria-label="Previous">←</button>
        <button @click="scrollBy(1)" aria-label="Next">→</button>
      </div>
    </div>

    <div class="viewport" ref="viewport">
      <div class="rail" ref="rail">
        <article v-for="s in items" :key="s.title" class="cell">
          <div class="diagram" v-html="s.svg"></div>
          <h3 class="display">{{ s.title }}</h3>
          <p>{{ s.desc }}</p>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import { maskReveal, gsap } from '@/composables/useReveal'
import { ScrollSmoother } from 'gsap/ScrollSmoother'

export default {
  name: 'SolutionsSection',
  data() {
    return {
      items: [
        { title: 'Laptop Repair', desc: 'Full diagnostics and board-level repair for all laptop brands.', svg: this.icoLaptop() },
        { title: 'Screen Replacement', desc: 'Cracked or dead displays — LCD/LED replacement for any model.', svg: this.icoScreen() },
        { title: 'Hardware Upgrade', desc: 'SSD, RAM and component upgrades for faster, longer-lasting machines.', svg: this.icoChip() },
        { title: 'Desktop / Computer Repair', desc: 'Complete desktop and PC diagnostics with reliable board-level repair.', svg: this.icoDesktop() },
        { title: 'Data Recovery', desc: 'Recover lost files from failed drives, corrupted disks and dead devices.', svg: this.icoDrive() },
        { title: 'All-in-One PC Repair', desc: 'Specialist repair for all-in-one PCs — display, board and internals.', svg: this.icoAllInOne() },
        { title: 'Monitor Repair', desc: 'Fix flickering, dead pixels and power faults across all monitor types.', svg: this.icoMonitor() }
      ]
    }
  },
  mounted() {
    maskReveal(this.$refs.head.querySelectorAll('.ln span'), { trigger: this.$el, start: 'top 80%' })
    this.mq = window.matchMedia('(min-width: 861px) and (pointer: fine)')
    if (this.mq.matches) this.enablePinScroll()
    else {
      gsap.from(this.$el.querySelectorAll('.cell'), {
        yPercent: 10, opacity: 0, duration: 1, ease: 'power4.out', stagger: 0.1,
        scrollTrigger: { trigger: this.$refs.rail, start: 'top 88%' }
      })
      this.enableDrag()
    }
  },
  beforeUnmount() {
    this._dragCleanup && this._dragCleanup()
    this._pinTrigger && this._pinTrigger.kill()
  },
  methods: {
    enablePinScroll() {
      const rail = this.$refs.rail
      // total horizontal overflow to travel while pinned
      const distance = () => Math.max(0, rail.scrollWidth - rail.clientWidth)
      const tween = gsap.to(rail, {
        x: () => -distance(),
        ease: 'none',
        scrollTrigger: {
          trigger: this.$refs.viewport,
          start: 'top top',
          end: () => '+=' + distance(),
          scrub: 1,
          pin: true,
          anticipatePin: 1,
          snap: { snapTo: 1 / (this.items.length - 1), duration: 0.25, ease: 'power1.inOut' },
          invalidateOnRefresh: true
        }
      })
      this._pinTrigger = tween.scrollTrigger
      gsap.from(this.$el.querySelectorAll('.cell'), {
        yPercent: 12, opacity: 0, duration: 1, ease: 'power4.out', stagger: 0.08,
        scrollTrigger: { trigger: this.$refs.viewport, start: 'top 70%' }
      })
    },
    scrollBy(dir) {
      if (this._pinTrigger) {
        const st = this._pinTrigger
        const step = 1 / (this.items.length - 1)
        const target = st.start + (st.end - st.start) * gsap.utils.clamp(0, 1, st.progress + dir * step)
        const smoother = ScrollSmoother.get && ScrollSmoother.get()
        if (smoother) smoother.scrollTo(target, true)
        else window.scrollTo({ top: target, behavior: 'smooth' })
        return
      }
      const rail = this.$refs.rail
      rail.scrollBy({ left: dir * rail.clientWidth * 0.5, behavior: 'smooth' })
    },
    enableDrag() {
      const rail = this.$refs.rail
      let down = false, startX = 0, startScroll = 0, moved = false
      const start = (e) => { down = true; moved = false; startX = e.pageX; startScroll = rail.scrollLeft; rail.classList.add('grabbing') }
      const move = (e) => {
        if (!down) return
        const dx = e.pageX - startX
        if (Math.abs(dx) > 4) moved = true
        rail.scrollLeft = startScroll - dx
      }
      const end = () => { down = false; rail.classList.remove('grabbing') }
      const click = (e) => { if (moved) { e.preventDefault(); e.stopPropagation() } }
      rail.addEventListener('pointerdown', start)
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', end)
      rail.addEventListener('click', click, true)
      this._dragCleanup = () => {
        rail.removeEventListener('pointerdown', start)
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', end)
        rail.removeEventListener('click', click, true)
      }
    },
    ico(body) {
      return `<svg viewBox="0 0 180 180"><g fill="none" stroke="#2a2a2a" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">${body}</g></svg>`
    },
    icoLaptop() {
      return this.ico(`
        <rect x="46" y="52" width="88" height="58" rx="4"/>
        <rect x="56" y="62" width="68" height="38" rx="2" stroke-width="0.8"/>
        <path d="M32 118 h116 l-8 12 H40 z"/>
        <line x1="78" y1="124" x2="102" y2="124"/>
      `)
    },
    icoScreen() {
      return this.ico(`
        <rect x="36" y="40" width="108" height="72" rx="4"/>
        <line x1="90" y1="112" x2="90" y2="132"/>
        <line x1="68" y1="132" x2="112" y2="132"/>
        <path d="M70 58 l16 20 l-10 6 l20 22" stroke-width="1"/>
        <path d="M104 54 l-6 18 l12 2 l-8 20" stroke-width="1"/>
      `)
    },
    icoChip() {
      let pins = ''
      for (let i = 0; i < 4; i++) {
        const p = 62 + i * 18
        pins += `<line x1="${p}" y1="52" x2="${p}" y2="42"/><line x1="${p}" y1="128" x2="${p}" y2="138"/>`
        pins += `<line x1="52" y1="${p}" x2="42" y2="${p}"/><line x1="128" y1="${p}" x2="138" y2="${p}"/>`
      }
      return this.ico(`
        <rect x="52" y="52" width="76" height="76" rx="4"/>
        <rect x="70" y="70" width="40" height="40" rx="2" stroke-width="0.9"/>
        ${pins}
      `)
    },
    icoDesktop() {
      return this.ico(`
        <rect x="54" y="40" width="46" height="100" rx="4"/>
        <line x1="66" y1="54" x2="88" y2="54"/>
        <circle cx="77" cy="72" r="5"/>
        <line x1="66" y1="118" x2="88" y2="126" stroke-width="0.9"/>
        <line x1="66" y1="110" x2="88" y2="118" stroke-width="0.9"/>
        <rect x="112" y="58" width="30" height="42" rx="3"/>
        <line x1="127" y1="100" x2="127" y2="116"/>
        <line x1="114" y1="116" x2="140" y2="116"/>
      `)
    },
    icoDrive() {
      return this.ico(`
        <path d="M60 36 h44 l20 20 v88 H60 z"/>
        <path d="M104 36 v20 h20" stroke-width="0.9"/>
        <path d="M74 104 a18 18 0 1 1 5 12" stroke-width="1.2"/>
        <path d="M74 88 v16 h16" stroke-width="1.2"/>
      `)
    },
    icoAllInOne() {
      return this.ico(`
        <rect x="40" y="36" width="100" height="76" rx="4"/>
        <rect x="50" y="46" width="80" height="56" rx="2" stroke-width="0.8"/>
        <path d="M78 112 h24 v10 h14 v6 H64 v-6 h14 z"/>
        <circle cx="90" cy="74" r="10" stroke-width="0.9"/>
        <path d="M90 68 v12 M84 74 h12" stroke-width="0.9"/>
      `)
    },
    icoMonitor() {
      let scan = ''
      for (let i = 0; i < 5; i++) scan += `<line x1="54" y1="${60 + i * 10}" x2="126" y2="${60 + i * 10}" stroke-width="0.6"/>`
      return this.ico(`
        <rect x="42" y="44" width="96" height="66" rx="4"/>
        ${scan}
        <line x1="90" y1="110" x2="90" y2="128"/>
        <line x1="68" y1="128" x2="112" y2="128"/>
      `)
    }
  }
}
</script>

<style scoped>
.sol { background: var(--grey); color: var(--ink); padding: clamp(50px, 6vw, 90px) 0 0; }
.sol-head {
  display: flex; align-items: flex-end; justify-content: space-between;
  width: min(1360px, 94vw); margin: 0 auto clamp(30px, 4vw, 50px);
}
.sol-head h2 { font-size: clamp(30px, 4vw, 56px); margin: 0; }
.sol-head h2 .ln { display: block; overflow: hidden; }
.sol-head h2 .ln span { display: inline-block; }
.arrows { display: flex; gap: 12px; }
.arrows button {
  width: 56px; height: 56px; border-radius: 50%; border: none; cursor: pointer;
  background: var(--ink); color: #fff; font-size: 20px; transition: background 0.25s, transform 0.2s;
}
.arrows button:hover { background: var(--coral); color: var(--ink); transform: scale(1.06); }
.viewport { overflow: hidden; border-top: 1px solid #9d9d9d; }
.rail {
  display: flex; overflow-x: auto; scroll-snap-type: x mandatory;
  scrollbar-width: none; cursor: grab; user-select: none;
}
.rail.grabbing { cursor: grabbing; scroll-snap-type: none; }
.rail::-webkit-scrollbar { display: none; }

/* Desktop: GSAP-driven horizontal travel while section is pinned */
@media (min-width: 861px) and (pointer: fine) {
  .rail { overflow: visible; scroll-snap-type: none; cursor: default; will-change: transform; }
  .cell { flex: 0 0 clamp(340px, 30vw, 460px); }
}
.cell .diagram :deep(svg) { transition: transform 0.6s var(--ease); }
.cell:hover .diagram :deep(svg) { transform: scale(1.06) rotate(2deg); }
.cell {
  flex: 0 0 clamp(280px, 33.33%, 460px);
  scroll-snap-align: start;
  padding: clamp(30px, 3vw, 50px);
  border-right: 1px solid #9d9d9d;
  min-height: 460px;
  display: flex; flex-direction: column;
}
.diagram { flex: 1; display: flex; align-items: center; justify-content: center; }
.diagram :deep(svg) { width: 62%; max-width: 220px; }
.cell h3 { font-size: clamp(22px, 2vw, 30px); letter-spacing: -0.8px; margin: 20px 0 8px; }
.cell p { color: #45454a; font-size: 14.5px; line-height: 1.55; margin: 0; }
@media (max-width: 860px) { .cell { flex-basis: 82%; } }
</style>
